// src\pages\Order\Sales Order\Sales Order Entry\salesOrderSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { fetchSalesOrderNumber, createSalesOrder } from "./salesOrderThunk";
import {
  pathSpliter,
  dateTimeFormat,
  currentDateTime,
  formatToCompanyTimezone,
  getDateTimeFromSelectedDate,
  formatToUtc,
  subscriptionMap,
} from "../../../../lib/functions";
import Big from "big.js";

// Discount for a single item row: reads whichever mode is active
// (percent, the default/undefined-safe mode, or a flat amount), clamped
// so a flat amount can never exceed the item's own line total.
const getItemDiscountAmount = (item, line) => {
  if (item?.discountMode === "amount") {
    const raw = new Big(item?.discountAmt || 0);
    return raw.gt(line) ? line : raw.lt(0) ? new Big(0) : raw;
  }
  return line.times(item?.discount || 0).div(100);
};

const initialState = {
  // Customer & Items
  customerInfo: {},
  itemCart: [], // renamed from orderItemsList
  payServiceCart: [],

  // Footer values
  footerValues: {
    subtotal: "0.00",
    totalTax: "0.00",
    grandTotal: "0.00",
    total_discount: "0.00",
    total_transport_cost: "0.00",
    description: "",
    paidAmount: 0,
    dueAmount: 0,
    payServiceCart: [], // ✅ additional expenses cart
    serviceCharge: 0,
    discountPct: 0,
    discountAmt: 0,
    taxPct: 0,
  },

  // Extra masterData fields
  tagFieldId: null,
  selectedDiscountAcc: null,
  selectedTaxAcc: null,
  selectedTransportAcc: null,
  conversionRate: 0,
  conversionCurrency: null,

  // System values
  // voucherNumber: null,
  sale_o_id: null,
  entryDate: currentDateTime(),
  dueDate: null,
  poReference: "",
  paymentTerm: 0,
  paymentTermsText: "Immediate",

  selectedOrderDetails: null,
  loading: false,
  success: null,
  taxMode: "on_item",
};

const salesOrderSlice = createSlice({
  name: "salesOrder",
  initialState,
  reducers: {
    resetSalesOrder(state) {
      return { ...initialState };
    },

    clearOrderForCustomerChange(state) {
      state.itemCart = [];
      state.footerValues = initialState.footerValues;
      state.paymentTerm = null;
      state.dueDays = null;
      state.paymentTermsText = "";
    },


    // Customer
    setCustomer(state, action) {
      state.customerInfo = action.payload || {};
    },

    // PO reference
    setPoReference(state, action) {
      state.poReference = action.payload || "";
    },

    // Payment Term
    setPaymentTerm(state, action) {
      const days = action.payload;

      state.paymentTerm = days;

      if (!days || days === 0) {
        state.paymentTermsText = "Immediate";
      } else if (days === 1) {
        state.paymentTermsText = "Next Day";
      } else {
        state.paymentTermsText = `${days} Days`;
      }
    },

    

    // Items
    addOrderRow(state, action) {
      state.itemCart.push(action.payload);
    },
    deleteOrderRow(state, action) {
      state.itemCart = state.itemCart.filter((r) => r.key !== action.payload);
    },
    updateOrderRow(state, action) {
      const { rowKey, updates } = action.payload;
      state.itemCart = state.itemCart.map((row) =>
        row.key === rowKey ? { ...row, ...updates } : row
      );
    },

    // Pay Service Cart
    setPayServiceCart(state, action) {
      const list = action.payload || [];
      state.payServiceCart = list;
      state.footerValues.payServiceCart = list; // ✅ sync for totals calc
    },
    // setPayServiceCart(state, action) {
    //   state.payServiceCart = action.payload || [];
    // },

    // Tag
    setTagFieldId(state, action) {
      state.tagFieldId = action.payload || null;
    },

    // Accounts (for discounts, tax, transport)
    setDiscountAcc(state, action) {
      state.selectedDiscountAcc = action.payload;
    },
    setTaxAcc(state, action) {
      state.selectedTaxAcc = action.payload;
    },
    setTransportAcc(state, action) {
      state.selectedTransportAcc = action.payload;
    },

    // Conversion
    // setConversion(state, action) {
    //   const { rate, currency } = action.payload || {};

    //   state.conversionRate = rate || 0;
    //   state.conversionCurrency = currency || null;
    // },

    setConversion(state, action) {
      state.conversionCurrency = action.payload || null;
    },
    setConversionRate(state, action) {
      state.conversionRate = action.payload;
    },

    // Dates
    setEntryDate(state, action) {
      state.entryDate = action.payload;
    },
    setDueDate(state, action) {
      state.dueDate = action.payload;
    },

    // Tax Mode
    setTaxMode(state, action) {
      state.taxMode = action.payload;
    },

    // Formfield
    setFormField(state, action) {
      const { field, value } = action.payload;
      state[field] = value;
    },

    setFooterValues(state, action) {
      state.footerValues = {
        ...state.footerValues,
        ...action.payload,
      };
    },

    loadSalesOrderForEdit(state, action) {
  const so = action.payload.order;
  
  state.sale_o_id = so.sale_o_id;
  state.tagFieldId = action.payload.tags.map(t => t.id);

  state.customerInfo = {
    acc_id: so.acc_id,
    acc_name: so.acc_name,
    address: so.address,
    contact_no: so.contact_no,
    institution_name: so.institution_name,
    credit_day: so.credit_day
  };

  state.itemCart = (so.details || []).map((d, index) => {
    // ✅ Same pattern as sales edit — rate 0 = bonus
    const isBonus = parseFloat(d.item_rate) === 0;

    return {
      key: index + 1,
      item_id: d.item_id,
      product: d.display_text || d.item_name,
      quantity: parseFloat(d.item_qty) || 0,
      rate: parseFloat(d.item_rate) || 0,
      discount: parseFloat(d.item_discount_per) || 0,
      discountAmt: parseFloat(d.item_discount) || 0,
      discountMode: d.discount_mode || "percent",
      tax_per: parseFloat(d.item_tax_per) || 0,
      bonus: isBonus,  // ✅ was hardcoded false before
      item_description: d.item_description || "",
      showDescription: !!d.item_description,
    };
  });

  const serviceCart = (so.details || []).flatMap(
    (d) => d.otherServiceTrans || []
  );

  state.footerValues = {
    ...state.footerValues,
    subtotal: so.sub_total,
    total_discount: so.total_discount,
    discountPct: parseFloat(so.total_discount_per) || 0,
    discountAmt: parseFloat(so.total_discount) || 0,
    discountMode: so.total_discount_mode || "percent",
    totalTax: so.total_tax,
    grandTotal: so.total_amount,

    payServiceCart: serviceCart,
    addExpenses: serviceCart.length > 0,
  };

  state.voucherNumber = so.sale_order_no;
  state.entryDate = so.created_date;
  state.paymentTerm = so.due_date || 0;  
  state.poReference = so.customer_po_ref || "";
  state.taxMode = so.is_cal_type === "individual" ? "on_item": "on_total";

  if (!state.paymentTerm || state.paymentTerm === 0) {
        state.paymentTermsText = "Immediate";
      } else if (state.paymentTerm === 1) {
        state.paymentTermsText = "Next Day";
      } else {
        state.paymentTermsText = `${state.paymentTerm} Days`;
      } 
},


    calculateTotals(state, action) {
      const authInfo = action?.payload?.authInfo || {};
      const isIndia = authInfo?.country?.toLowerCase() === "india";
      const gstEnabled = authInfo?.gst_enabled === "yes";
      const showTax = !isIndia || gstEnabled;
      let subtotal = new Big(0);
      let totalDiscount = new Big(0);
      let totalTax = new Big(0);

      state.itemCart.forEach((it) => {
        const qty = new Big(it.quantity || 0);
        const rate = new Big(it.rate || 0);

        // skip bonus items (they have no value)
        if (it.bonus) {
          return;
        }

        let line = qty.times(rate);
   
        if (state.taxMode === "on_item") {
          // discount first
          const discountAmt = getItemDiscountAmount(it, line);
          const afterDiscount = line.minus(discountAmt);
          line = line.minus(discountAmt);

          // tax after discount
          const taxPer = showTax ? new Big(it.tax_per || 0) : new Big(0);
          const taxAmt = (afterDiscount.times(taxPer)).div(100);
          line = line.plus(taxAmt);

          subtotal = subtotal.plus(line);
          totalDiscount = totalDiscount.plus(discountAmt);
          totalTax = totalTax.plus(taxAmt);
        } else {
          // on_total = subtotal only, footer applies later
          subtotal = subtotal.plus(line);
        }
      });

      let resolvedDiscountPct = state.footerValues.discountPct;

      if (state.taxMode === "on_total") {
        const footerDiscountMode = state.footerValues.discountMode === "amount" ? "amount" : "percent";
        if (footerDiscountMode === "amount") {
          const raw = new Big(state.footerValues.discountAmt || 0);
          totalDiscount = raw.gt(subtotal) ? subtotal : raw.lt(0) ? new Big(0) : raw;
          // Back-computed so the printed voucher (which reads this field
          // directly) is correct even when the footer is in amount mode.
          resolvedDiscountPct = subtotal.eq(0) ? 0 : Number(totalDiscount.div(subtotal).times(100).toFixed(13));
        } else {
          const discountPct = new Big(state.footerValues.discountPct || 0);
          totalDiscount = subtotal.times(discountPct).div(100);
          resolvedDiscountPct = discountPct.toNumber();
        }

        const afterDiscount = subtotal.minus(totalDiscount);

        const authTax = showTax ? new Big(authInfo?.item_tax || 0) : new Big(0);
        totalTax = afterDiscount.times(authTax).div(100);
      }

      const serviceCharge = new Big(state.footerValues.serviceCharge || 0);
      const expenseTotal = (state.footerValues.payServiceCart || []).reduce(
        (sum, r) => sum.plus(r.tran_amount || 0),
        new Big(0)
      );

      // const grandTotal = subtotal - totalDiscount + totalTax + serviceCharge + expenseTotal;

      const grandTotal =
        state.taxMode === "on_item"
          ? subtotal.plus(serviceCharge).plus(expenseTotal)
          : subtotal.minus(totalDiscount).plus(totalTax).plus(serviceCharge).plus(expenseTotal);
      
      const paid = new Big(state.footerValues.paidAmount || 0);

      state.footerValues = {
        ...state.footerValues,
        subtotal: subtotal.toFixed(2),
        total_discount: totalDiscount.toFixed(2),
        discountPct: resolvedDiscountPct,
        totalTax: totalTax.toFixed(2),
        serviceCharge: serviceCharge.toFixed(2),
        grandTotal: grandTotal.toFixed(2),
        dueAmount: (
          grandTotal.minus(paid)
        ).toFixed(2),
      };
    },

    // mapping for conversion of quotation to sales order
    loadQuotationForSalesOrder(state, action) {
      const q = action.payload;
   
      // ✅ Customer mapping (display format)
      state.customerInfo = {
        acc_code: q?.acc_code,
        acc_id: q.acc_id,
        acc_name: q.acc_name,
        acc_type_id: q?.acc_type_id || 0,
        acc_type_name: q?.acc_type_name || "",
        address: q.address,
        contact_no: q.contact_no,
        country_code: q?.country_code || "1",
        credit_day: q?.credit_day || null,
        credit_limit: q?.credit_limit || 0,
        credit_month: q?.credit_month || "",
        display_text: `${q.acc_name}`,
        employee_id: q?.employee_id || 0,
        group_name: q?.group_name || null,
        institution_name: q.institution_name,
        party_type: q?.party_type || "",
      };

state.itemCart = (q.details || []).map((d, index) => {
  const isBonus =
    d.is_bonus === 1 ||
    d.is_bonus === "yes" ||
    d.is_bonus === true ||
    parseFloat(d.item_rate) === 0; // ✅ same fallback

  return {
    key: index + 1,
    item_id: d.item_id,
    product: d.display_text || d.item_name,
    is_service: d.is_service,
    quantity: d.item_qty,
    rate: isBonus ? 0 : Number(d.item_rate || 0), // ✅ force 0 for bonus
    discount: Number(d.item_discount_per || 0),
    discountAmt: Number(d.item_discount || 0),
    discountMode: d.discount_mode || "percent",
    tax_per: Number(d.item_tax_per || 0),
    bonus: isBonus,
    item_description: d.item_description || "",
    showDescription: !!d.item_description,
    isFromQuotation: true,
  };
});
        const serviceCart = (q.otherServiceTrans || []).map((t, idx) => ({
    key: `expense_${idx}`,
    to_acc_id: t.to_acc_id,
    to_acc_name: t.to_acc_name,
    tran_amount: parseFloat(t.tran_amount) || 0,
    acc_type_id: t.acc_type_id || "direct_income",
  }));

  state.payServiceCart = serviceCart;

      // Footer values
      state.footerValues = {
        ...state.footerValues,
        subtotal: q.sub_total,
        total_discount: q.total_discount,
        totalTax: q.total_tax,
        grandTotal: q.total_amount,
        serviceCharge: q.service_charge || 0,
            addExpenses: serviceCart.length > 0,
      };

      // Extra expenses
      state.payServiceCart = q.otherServiceTrans || [];
      state.footerValues.payServiceCart = q.otherServiceTrans || [];

      // Set entry date & terms
      state.entryDate = q.created_date;
      state.paymentTerm = q.credit_day || 0;
      // state.paymentTerm = q.due_date || 0; 
      if (!state.paymentTerm || state.paymentTerm === 0) {
        state.paymentTermsText = "Immediate";
      } else if (state.paymentTerm === 1) {
        state.paymentTermsText = "Next Day";
      } else {
        state.paymentTermsText = `${state.paymentTerm} Days`;
      } 
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesOrderNumber.fulfilled, (state, action) => {
        state.voucherNumber = action.payload;
      })
      .addCase(createSalesOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSalesOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload;
      })
      .addCase(createSalesOrder.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  resetSalesOrder,
  clearOrderForCustomerChange,
  setCustomer,
  addOrderRow,
  deleteOrderRow,
  updateOrderRow,
  calculateTotals,
  setPayServiceCart,
  setNarration,
  setTagFieldId,
  setDiscountAcc,
  setTaxAcc,
  setTransportAcc,
  setConversion,
  setEntryDate,
  setDueDate,
  setFormField,
  setFooterValues,
  setTaxMode,
  setPoReference,
  setPaymentTerm,
  loadQuotationForSalesOrder,
  loadSalesOrderForEdit,
  setConversionRate
} = salesOrderSlice.actions;

export default salesOrderSlice.reducer;
